import { useState } from 'react'
import styles from './AddSourceModal.module.css'
import AssociatedProductInput from './AssociatedProductInput'
import Icon from './Icon'
import { ACCEPTED_DOCUMENT_EXTENSIONS, MAX_NAME_LENGTH } from './sourceVisuals'
import { nameFromFilename, nameFromUrl } from '../utils/sourceName'

const EMPTY_FORM = { name: '', description: '', products: [], productVersion: '', reviewDate: '', url: '', file: null }

/**
 * The Name to offer for what's been chosen so far — the file's name for a
 * document, the URL's page for a web source. '' when there's nothing to go on
 * yet, which is also what a half-typed URL gives. See utils/sourceName.
 */
function suggestedNameFor(sourceType, { file, url }) {
  return sourceType === 'document'
    ? nameFromFilename(file?.name, MAX_NAME_LENGTH)
    : nameFromUrl(url, MAX_NAME_LENGTH)
}

function UploadIcon() {
  return <Icon name="upload" />
}

function LinkIcon() {
  return <Icon name="link" />
}

/**
 * AddSourceModal — register a NEW document (file upload) or web source (URL) to
 * be indexed into the Knowledge Base. Names are unique per tenant; covering
 * another product version of a source that already exists goes through
 * AddVersionModal instead.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {function(): void} props.onClose
 * @param {string[]} [props.products] — tenant's product catalog, for the Products field
 * @param {function({ type: 'document'|'web', name: string, description: string, products: string[], productVersion: string, reviewDate: string, url?: string, file?: File }): Promise<void>} props.onSubmit
 */
export default function AddSourceModal({ isOpen, onClose, products = [], onSubmit }) {
  const [sourceType, setSourceType] = useState('document')
  const [form, setForm] = useState(EMPTY_FORM)
  const [dragActive, setDragActive] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  // Whether Name holds the user's own wording. A suggestion never overwrites it.
  const [nameTouched, setNameTouched] = useState(false)

  if (!isOpen) return null

  function updateField(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  /**
   * Re-derive the Name from whatever the source now is, unless the user has
   * written one. Suggesting leaves the field untouched-by-the-user, so a second
   * file (or a corrected URL) re-derives rather than leaving the first guess.
   */
  function applySourceChange(sourceType, changes) {
    setForm(prev => {
      const next = { ...prev, ...changes }
      return nameTouched ? next : { ...next, name: suggestedNameFor(sourceType, next) }
    })
  }

  function switchType(type) {
    setSourceType(type)
    setError(null)
    // The other tab's input is what the name came from, so it stops applying —
    // and switching back re-derives, since whatever was chosen is still there.
    applySourceChange(type, {})
  }

  function handleClose() {
    setForm(EMPTY_FORM)
    setSourceType('document')
    setDragActive(false)
    setError(null)
    setNameTouched(false)
    onClose()
  }

  function chooseFile(file) {
    applySourceChange('document', { file })
  }

  // On blur rather than on change: deriving per keystroke would walk the field
  // through "Htt", "Http" as the URL is typed. A paste followed by tabbing on
  // lands here immediately.
  function handleUrlBlur() {
    applySourceChange('web', {})
  }

  function handleDrop(e) {
    e.preventDefault()
    setDragActive(false)
    const file = e.dataTransfer.files?.[0]
    if (file) chooseFile(file)
  }

  async function handleSubmit(e) {
    e.preventDefault()

    if (!form.name.trim()) {
      setError('Name is required.')
      return
    }
    if (form.name.trim().length > MAX_NAME_LENGTH) {
      setError(`Name must be ${MAX_NAME_LENGTH} characters or fewer.`)
      return
    }
    if (sourceType === 'document' && !form.file) {
      setError('Choose a file to upload.')
      return
    }
    if (sourceType === 'document' && !ACCEPTED_DOCUMENT_EXTENSIONS.some(ext => form.file.name.toLowerCase().endsWith(ext))) {
      // Soft client-side gate — the real enforcement is the S3 upload policy's
      // Content-Type condition. Both are spoofable by a determined client;
      // this just gives normal users fast feedback before they wait on a
      // network round-trip.
      setError('Unsupported file type.')
      return
    }
    if (sourceType === 'web' && !form.url.trim()) {
      setError('Enter a URL to index.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      await onSubmit({
        type: sourceType,
        name: form.name.trim(),
        description: form.description.trim(),
        products: form.products,
        productVersion: form.productVersion.trim(),
        reviewDate: form.reviewDate,
        url: sourceType === 'web' ? form.url.trim() : undefined,
        file: sourceType === 'document' ? form.file : undefined,
      })
      handleClose()
    } catch (err) {
      setError(err.message || 'Failed to add source.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.backdrop}>
      <div className={styles.modal}>

        {/* Header */}
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>Add Source</h2>
            <p className={styles.subtitle}>Ingest a document or web source into the knowledge base</p>
          </div>
          <button className={styles.closeBtn} onClick={handleClose} aria-label="Close modal">
            <Icon name="close" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className={styles.body}>

            <div className={styles.typeToggle} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={sourceType === 'document'}
                className={`${styles.typeBtn} ${sourceType === 'document' ? styles.typeBtnActive : ''}`}
                onClick={() => switchType('document')}
              >
                <UploadIcon /> Document
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={sourceType === 'web'}
                className={`${styles.typeBtn} ${sourceType === 'web' ? styles.typeBtnActive : ''}`}
                onClick={() => switchType('web')}
              >
                <LinkIcon /> URL
              </button>
            </div>

            {error && <div className={styles.errorNotice}>{error}</div>}

            {/* Name leads the form and is emphasized because it is permanent: it
                is baked into the document's embeddings at ingest time, so it can
                only be set here, at creation, never edited later. */}
            <div className={styles.field}>
              <label className={styles.label} htmlFor="asm-name">Name</label>
              <input
                id="asm-name"
                className={styles.input}
                type="text"
                required
                maxLength={MAX_NAME_LENGTH}
                placeholder={sourceType === 'document' ? 'e.g. Codebeamer quick start guide' : 'e.g. Kepware Edge quick start guide'}
                value={form.name}
                onChange={e => {
                  updateField('name', e.target.value)
                  // Emptying the field asks for the suggestion back; anything
                  // else is wording of the user's own and is left alone.
                  setNameTouched(e.target.value.trim() !== '')
                }}
              />
              <span className={styles.hint}>
                Permanent — the name can’t be changed after the source is added. Choose it carefully.
              </span>
            </div>

            {sourceType === 'document' ? (
              <div key="document" className={styles.field}>
                <label
                  htmlFor="asm-file"
                  className={`${styles.dropzone} ${dragActive ? styles.dropzoneActive : ''}`}
                  onDragOver={e => { e.preventDefault(); setDragActive(true) }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                >
                  <UploadIcon />
                  {form.file ? (
                    <span className={styles.dropzoneFile}>{form.file.name}</span>
                  ) : (
                    <>
                      <span className={styles.dropzoneTitle}>Drag &amp; drop a document</span>
                      <span className={styles.dropzoneSub}>or click to browse your files</span>
                    </>
                  )}
                </label>
                <input
                  id="asm-file"
                  className={styles.hiddenFileInput}
                  type="file"
                  accept={ACCEPTED_DOCUMENT_EXTENSIONS.join(',')}
                  onChange={e => chooseFile(e.target.files?.[0] ?? null)}
                />
              </div>
            ) : (
              <div key="web" className={styles.field}>
                <label className={styles.label} htmlFor="asm-url">Source URL</label>
                <div className={styles.iconInputWrap}>
                  <LinkIcon />
                  <input
                    id="asm-url"
                    className={styles.iconInput}
                    type="text"
                    placeholder="https://docs.example.com/guide"
                    value={form.url}
                    onChange={e => updateField('url', e.target.value)}
                    onBlur={handleUrlBlur}
                  />
                </div>
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.label} htmlFor="asm-description">
                Description <span className={styles.optional}>(optional)</span>
              </label>
              <textarea
                id="asm-description"
                className={styles.textarea}
                rows={3}
                placeholder="Briefly describe what this source covers"
                value={form.description}
                onChange={e => updateField('description', e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="asm-products">Products</label>
              <AssociatedProductInput
                id="asm-products"
                selected={form.products}
                availableProducts={products}
                onChange={selected => updateField('products', selected)}
                placeholder="Link a product…"
              />
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="asm-product-version">
                  Product Version <span className={styles.optional}>(optional)</span>
                </label>
                <input
                  id="asm-product-version"
                  className={styles.input}
                  type="text"
                  placeholder="e.g. 25.1"
                  value={form.productVersion}
                  onChange={e => updateField('productVersion', e.target.value)}
                />
                <span className={styles.hint}>Blank means it applies to all versions.</span>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="asm-review-date">Review Date</label>
                <input
                  id="asm-review-date"
                  className={styles.input}
                  type="date"
                  value={form.reviewDate}
                  onChange={e => updateField('reviewDate', e.target.value)}
                />
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <button
              type="button"
              className={`${styles.footerBtn} ${styles.cancelBtn}`}
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${styles.footerBtn} ${styles.submitBtn}`}
              disabled={submitting}
            >
              {submitting ? 'Adding…' : 'Add Source'}
            </button>
          </div>
        </form>

      </div>
    </div>
  )
}
